import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ArrowRight, ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Container, Eyebrow, Section, ArrowLink } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { RoomCard } from "@/components/shared/RoomCard";
import { ProgrammeAgenda } from "@/components/shared/Programme";
import { HomeHero } from "@/components/shared/Hero";
import { QuickBook } from "@/components/shared/QuickBook";
import { WordWall } from "@/components/shared/Decor";
import { IntentDoors } from "@/components/shared/Doors";
import { Img, SIZES } from "@/components/shared/Img";
import { PhotoNumberTag } from "@/components/shared/PhotoNumber";
import { Seo, orgJsonLd } from "@/components/shared/Seo";
import { fetchContent, fetchEvents, fetchPosts, fetchRooms, fetchSite, preloadImages, whenIdle } from "@/lib/api";
import { PostCard } from "@/components/shared/PostCard";
import { fmtPrice, minPrice, floorLabel, paragraphs } from "@/lib/format";
import { cn } from "@/lib/utils";

const firstSentences = (text = "", n = 2) => (text.match(/[^.!?]+[.!?]+/g) || [text]).slice(0, n).join(" ").trim();
const isOwnPhoto = (src) => typeof src === "string" && src.startsWith("/");
const roomImg = (rooms, slug, i = 0) => (rooms || []).find((r) => r.slug === slug)?.images?.[i];

/** Cropped photo in a soft-cornered frame. Children render on top of the image. */
const Photo = ({ src, alt = "", className, imgClassName, width = 1200, sizes = SIZES.half, priority = false, testId, imgTestId, children }) => (
  <figure className={cn("relative overflow-hidden rounded-[12px] bg-secondary", className)} data-testid={testId}>
    {src ? (
      <Img src={src} alt={alt} width={width} sizes={sizes} priority={priority} className={cn("absolute inset-0 h-full w-full object-cover", imgClassName)} data-testid={imgTestId} />
    ) : (
      <Skeleton className="absolute inset-0 rounded-none" />
    )}
    {children}
  </figure>
);

/* ------------------------------------------------------------------ */
/* Rooms: photographed rooms as tiles, the rest as a hairline index     */
/* ------------------------------------------------------------------ */
const spanFor = (i, n) => {
  if (i === 0) return "lg:col-span-7";
  if (i === 1) return "lg:col-span-5";
  const tail = n - 2;
  const rem = tail % 3;
  const pos = i - 2;
  if (rem === 0) return "lg:col-span-4";
  if (rem === 2) return pos >= tail - 2 ? "lg:col-span-6" : "lg:col-span-4";
  return pos >= tail - 4 ? "lg:col-span-6" : "lg:col-span-4";
};
const aspectFor = (i, span) => {
  if (i === 0) return "aspect-[4/3] lg:aspect-[3/2]";
  if (i === 1) return "aspect-[4/3] lg:aspect-auto lg:h-full";
  return span === "lg:col-span-6" ? "aspect-[4/3] lg:aspect-[16/10]" : "aspect-[4/3]";
};

const RoomIndexRow = ({ room, whole = false }) => {
  const price = minPrice(room);
  const closed = room.status === "closed";
  const meta = whole ? room.description : [floorLabel(room.floor), room.capacity ? `${room.capacity} personar` : room.capacity_note].filter(Boolean).join(" · ");
  return (
    <li className="border-b border-border">
      <Link
        to={whole ? `/leig-rom?rom=${room.id}` : `/rom/${room.slug}`}
        className="group grid grid-cols-[1fr_auto] items-baseline gap-x-6 gap-y-1.5 py-5 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto_auto] sm:items-center sm:gap-x-8"
        data-testid={whole ? "home-whole-house-book" : `home-room-row-${room.slug}`}
      >
        <h3 className={cn("t-h3 u-hover", closed && "text-muted-foreground")}>{room.name}</h3>
        <p className="order-last col-span-2 text-[15px] leading-snug text-muted-foreground line-clamp-2 sm:order-none sm:col-span-1 sm:line-clamp-1">{meta}</p>
        <span className={cn("whitespace-nowrap text-[14px] font-medium tabular", closed ? "text-muted-foreground" : "text-foreground")}>
          {closed ? "Mellombels stengt" : price ? `frå ${fmtPrice(price)}` : "Etter avtale"}
        </span>
        <span className="hidden h-9 w-9 items-center justify-center rounded-full bg-secondary transition-colors group-hover:bg-foreground group-hover:text-background sm:flex"><ArrowRight className="h-4 w-4" /></span>
      </Link>
    </li>
  );
};

/* ------------------------------------------------------------------ */
/* Film strip: mixed-ratio photos that bleed off the right edge         */
/* ------------------------------------------------------------------ */
const STRIP = [
  // the board's own photograph of the kitchen in full swing – a fixed reference into the image library
  { src: "/api/images/709f3743-377e-4079-a16c-841fecb3023b", caption: "Full fart på kjøkkenet", aspect: "aspect-[5/3]" },
  { slug: "king-oscar-amfi", i: 5, caption: "Julekonsert i King Oscar Amfi", aspect: "aspect-[3/2]" },
  { slug: "king-oscar-amfi", i: 2, caption: "Band på scena", aspect: "aspect-[16/9]" },
  { slug: "mussa", i: 1, caption: "Kaffimat før møtet", aspect: "aspect-[4/5]" },
  { src: "/api/images/92782492-ef87-480d-b1a0-3525ccd32a27", caption: "Baren i Stovene", aspect: "aspect-[16/9]" },
  { slug: "kippers-kafe", i: 2, caption: "Disken i Kippers Kafé", aspect: "aspect-[3/4]" },
  { slug: "kippers-kafe", i: 1, caption: "Kippers Kafé", aspect: "aspect-[3/4]" },
  { slug: "mussa", i: 2, caption: "Iskaffi med fjorden som bakgrunn", aspect: "aspect-square" },
  { src: "/api/images/08f67159-5d74-449a-8d05-980baa225f4a", caption: "Kveld i Stovene", aspect: "aspect-[4/3]" },
  { src: "/api/images/7bab9055-7c6b-4cb3-8447-0c2a7eaf4867", caption: "Salongen i Stovene", aspect: "aspect-[4/3]" },
  { src: "/api/images/cd213642-a613-4f87-86f7-321020357b1f", caption: "Pause i foajeen", aspect: "aspect-[16/10]" },
  { slug: "king-oscar-amfi", i: 1, caption: "Fullt hus", aspect: "aspect-[3/2]" },
];
const STRIP_FIXED = new Set(STRIP.map((s) => s.src).filter(Boolean));

const FilmStrip = ({ items }) => {
  const ref = useRef(null);
  const go = (dir) => ref.current?.scrollBy({ left: dir * Math.max(360, ref.current.clientWidth * 0.6), behavior: "smooth" });
  if (!items.length) return null;
  return (
    <section id="livet" className="scroll-mt-24 overflow-x-clip bg-secondary pb-14 pt-16 sm:pb-16 sm:pt-20 lg:pb-20 lg:pt-24" data-testid="home-strip">
      <Container>
        <div className="flex items-end justify-between gap-6">
          <div className="max-w-2xl">
            <Eyebrow num="03">Livet i huset</Eyebrow>
            <h2 className="t-h2 mt-4 text-balance">Her skjer det <em className="t-swash">litt av kvart</em></h2>
            <p className="t-lead mt-5 max-w-2xl text-muted-foreground text-pretty">Konsertar og korøving. Kaffi og møte. Bryllaup, selskap og gode samtalar. Halsnøy Kulturhus er eit hus som skal brukast – av heile øya.</p>
          </div>
          <div className="hidden gap-2 lg:flex">
            <button type="button" onClick={() => go(-1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-foreground/20 transition-colors hover:bg-foreground hover:text-background" aria-label="Bla bakover" data-testid="home-strip-prev"><ArrowLeft className="h-4 w-4" /></button>
            <button type="button" onClick={() => go(1)} className="flex h-11 w-11 items-center justify-center rounded-full border border-foreground/20 transition-colors hover:bg-foreground hover:text-background" aria-label="Bla framover" data-testid="home-strip-next"><ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div ref={ref} className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 pr-5 sm:pr-8 lg:mt-12 lg:pr-10" style={{ marginRight: "calc(50% - 50vw)" }}>
          {items.map((it, i) => (
            <figure key={it.src} className="shrink-0 snap-start">
              <div className={cn("img-hover relative h-[210px] overflow-hidden rounded-[12px] bg-foreground/5 sm:h-[340px] lg:h-[460px]", it.aspect)}>
                <Img src={it.src} alt={it.caption} width={1200} sizes="(min-width: 1024px) 700px, (min-width: 640px) 520px, 320px" className="h-full w-full object-cover" />
                <PhotoNumberTag src={it.src} interactive className="absolute right-2.5 top-2.5" testId={`home-strip-number-${i}`} />
              </div>
              <figcaption className="mt-3 flex items-baseline gap-3 text-[14px]">
                <span className="t-mono-sm text-muted-foreground tabular">{String(i + 1).padStart(2, "0")}</span>
                <span>{it.caption}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Container>
    </section>
  );
};

/* ------------------------------------------------------------------ */
/* Small numeric figure for the about block                            */
/* ------------------------------------------------------------------ */

/* Photographs chosen by the board for the hero carousel – fixed references into the house's own library
   (/api/images), so the hero renders at once instead of waiting for the room data. In order:
   the singer on stage · a full King Oscar Amfi · the summer tent concert · the bar in Stovene at dusk. */
export const HERO_SLIDES = [
  "/api/images/8978150d-51e7-43a0-8d41-7d194a876a25",
  "/api/images/293d399d-0eb9-4bf6-8327-6b8ce3857b91",
  "/api/images/2895fc43-924b-4892-b762-964d873cf6b2",
  "/api/images/92782492-ef87-480d-b1a0-3525ccd32a27",
];

const INTENTS = [
  { id: "oppleve", to: "/arrangement", eyebrow: "Oppleve", title: "Sjå kva som skjer", text: "Konsertar, quiz, teater og møte – med billettar rett på mobilen." },
  { id: "leige", to: "/leig-rom", eyebrow: "Leige", title: "Leig eit rom", text: "Selskap, møte, kurs eller øving. Sjå pris og ledig dato med ein gong." },
  { id: "arrangere", to: "/arranger", eyebrow: "Arrangere", title: "Arranger noko", text: "Scene, kafé og billettløysing. Pengane går rett til deg." },
];

const STEPS = [
  { title: "Sjekk kalenderen", text: "Sjå kva rom som er ledige på datoen du ønskjer. Private arrangement vert viste utan namn." },
  { title: "Send ein førespurnad", text: "Vel rom, dato og kva du planlegg. Du ser med ein gong om det er noko som kolliderer." },
  { title: "Få stadfesting frå styret", text: "Styret går gjennom førespurnaden og svarar på e-post. Bookinga er først bindande når ho er stadfesta." },
];

export default function Home() {
  const { data: content } = useQuery({ queryKey: ["content"], queryFn: fetchContent });
  const { data: rooms, isLoading: roomsLoading } = useQuery({ queryKey: ["rooms"], queryFn: fetchRooms });
  const { data: events, isLoading: eventsLoading } = useQuery({ queryKey: ["events"], queryFn: () => fetchEvents({ limit: 5 }) });
  const { data: site } = useQuery({ queryKey: ["site"], queryFn: fetchSite });
  const { data: posts } = useQuery({ queryKey: ["posts", "home"], queryFn: () => fetchPosts({ limit: 3 }), staleTime: 60_000 });

  const hero = content?.heim_hero;
  const om = content?.om_huset;
  const nextEvent = events?.[0] || null;
  const title = hero?.title || "Huset eigd av folket på Halsnøy";

  const list = (rooms || []).filter((r) => !r.is_whole_house);
  const whole = (rooms || []).find((r) => r.is_whole_house);
  const featured = list.filter((r) => isOwnPhoto(r.images?.[0]));
  const rest = list.filter((r) => !isOwnPhoto(r.images?.[0]));

  // Photography – all from the house's own library, with graceful fallbacks.
  // The facade (admin "hero" image) is the house's portrait – it opens «Om huset» and is the share image.
  const facadeImg = hero?.images?.[0];
  // The hero shows life, not architecture – see HERO_SLIDES for the board's order.
  const heroSlides = HERO_SLIDES;
  const programImg = roomImg(rooms, "king-oscar-amfi", 3) || roomImg(rooms, "king-oscar-amfi", 2);
  const aboutImg = facadeImg || roomImg(rooms, "stovene", 0) || om?.images?.[0];
  const panorama = content?.omradet?.images?.[0] || hero?.images?.[1];
  const used = new Set([facadeImg, ...heroSlides, aboutImg, panorama, programImg]);
  // Fixed `src` entries are deliberate picks and may repeat a picture shown elsewhere on the page.
  const strip = STRIP.map((s) => ({ ...s, src: s.src || roomImg(rooms, s.slug, s.i) })).filter((s) => isOwnPhoto(s.src) && (STRIP_FIXED.has(s.src) || !used.has(s.src)));

  // Facts straight from the room data.
  const amfi = (rooms || []).find((r) => r.slug === "king-oscar-amfi");
  const floors = new Set(list.map((r) => r.floor).filter((f) => f !== undefined && f !== null));
  const facts = [
    list.length ? { value: String(list.length), label: "rom å leige" } : null,
    amfi?.capacity ? { value: String(amfi.capacity), label: "plassar i amfiet" } : null,
    floors.size ? { value: String(floors.size), label: "etasjar" } : null,
    { value: "100 %", label: "dugnad" },
  ].filter(Boolean);

  // Warm the browser cache for the room covers (the next pages most visitors open) – but only once the hero has had
  // the network to itself, and never more than a handful of files.
  useEffect(() => {
    if (!rooms) return undefined;
    const covers = (rooms || []).map((r) => r.images?.[0]).filter(isOwnPhoto);
    return whenIdle(() => preloadImages(covers, 1200), 2500);
  }, [rooms]);

  const areaLinks = [
    { key: "turloyper", to: "/omradet#turloyper", alt: "Turløype på Halsnøy" },
    { key: "bubil", to: "/omradet#bubil", alt: "Bubilparkeringa ved kulturhuset" },
    { key: "bathamna", to: "/omradet#bathamna", alt: "Sæbøvik hamn i kveldssol" },
  ];

  return (
    <div data-testid="home-page">
      <Seo image={facadeImg} imageAlt="Halsnøy Kulturhus i Sæbøvik" jsonLd={orgJsonLd(site)} path="/" />
      {/* HERO ------------------------------------------------------- */}
      <HomeHero
        title={title}
        body={hero?.body}
        images={heroSlides}
        facts={facts}
        nextEvent={nextEvent}
        eventsLoading={eventsLoading}
      />

      {/* 01 PROGRAMME – grows out of the hero, before anything about renting ---- */}
      <ProgrammeAgenda events={events} loading={eventsLoading} facebookUrl={site?.facebook_url} />

      {/* THREE WAYS IN – oppleve · leige · arrangere ------------------ */}
      <section className="py-20 sm:py-24 lg:py-28" data-testid="home-intents">
        <Container>
          <div className="mb-8 flex flex-col gap-4 sm:mb-10 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <Eyebrow>Tre vegar inn</Eyebrow>
              <h2 className="t-h2 mt-4 text-balance">Kva vil du <em className="t-swash">gjere</em> på huset?</h2>
            </div>
            <p className="max-w-sm text-[16px] leading-relaxed text-muted-foreground text-pretty lg:pb-1.5">Oppleve noko, leige eit rom til ditt eige, eller arrangere for publikum. Vel døra som passar.</p>
          </div>
          <FadeUp>
            <IntentDoors items={INTENTS} />
          </FadeUp>
        </Container>
      </section>

      <QuickBook rooms={rooms} fallbackImage={facadeImg} />

      <WordWall />

      {/* 01 ROOMS --------------------------------------------------- */}
      <Section
        id="rom"
        num="02"
        eyebrow="Rom og prisar"
        className="pt-12 sm:pt-16 lg:pt-20"
        title={<>Sju rom. Tre etasjar. <em className="t-swash">Eitt hus</em> for heile øya.</>}
        subtitle="Frå amfiet med 187 plassar til små møterom med utsikt over sjøen. Her finn du rom for både dei store anledningane og dei små samlingane – med tydelege prisar og enkel booking."
        action={<ArrowLink to="/rom" data-testid="home-all-rooms-link">Sjå alle rom og prisar</ArrowLink>}
      >
        {roomsLoading ? (
          <div className="grid gap-3 lg:grid-cols-12">
            <Skeleton className="aspect-[4/3] rounded-[12px] lg:col-span-7 lg:aspect-[3/2]" />
            <Skeleton className="aspect-[4/3] rounded-[12px] lg:col-span-5 lg:aspect-auto" />
            {[0, 1, 2].map((i) => <Skeleton key={i} className="aspect-[4/3] rounded-[12px] lg:col-span-4" />)}
          </div>
        ) : (
          <>
            <div className="grid gap-3 lg:grid-cols-12" data-testid="home-rooms">
              {featured.map((r, i) => {
                const span = spanFor(i, featured.length);
                return <RoomCard key={r.id} room={r} index={i} className={cn(span, i === 1 && "lg:h-full")} aspect={aspectFor(i, span)} />;
              })}
            </div>
            {(rest.length > 0 || whole) && (
              <ul className="mt-10 border-t border-border sm:mt-12" data-testid="home-room-index">
                {rest.map((r) => <RoomIndexRow key={r.id} room={r} />)}
                {whole && <RoomIndexRow room={whole} whole />}
              </ul>
            )}
          </>
        )}
      </Section>

      {/* 02 LIFE IN THE HOUSE --------------------------------------- */}
      <FilmStrip items={strip} />

      {/* 03 HOW IT WORKS -------------------------------------------- */}
      <Section id="slik" num="04" eyebrow="Slik fungerer det" title={<>Frå idé til stadfesta booking på <em className="t-swash">tre enkle steg</em></>} tight>
        {/* three steps on one line – the same language as the doors: dots are people and moments, gold is you */}
        <ol className="relative grid gap-10 md:grid-cols-3 md:gap-8" data-testid="home-steps">
          <span aria-hidden="true" className="absolute left-[7px] top-2 hidden h-[calc(100%-1rem)] w-px bg-border md:left-0 md:top-[7px] md:h-px md:w-full md:block" />
          <span aria-hidden="true" className="absolute left-[7px] top-2 h-[calc(100%-1rem)] w-px bg-border md:hidden" />
          {STEPS.map((s, i) => (
            <li key={s.title} className="relative pl-8 md:pl-0 md:pt-10">
              <span aria-hidden="true" className={cn("absolute left-0 top-1 h-[15px] w-[15px] rounded-full ring-4 ring-background md:left-0 md:top-0", i === 0 ? "bg-sol" : "bg-foreground")} />
              <FadeUp delay={i * 0.08}>
                <p className="t-mono flex items-center gap-2.5 text-muted-foreground"><span className="tabular">0{i + 1}</span><span className="h-px w-5 bg-current" aria-hidden="true" /><span>{["Du sjekkar", "Du sender", "Styret svarar"][i]}</span></p>
                <h3 className="t-h3 mt-3">{s.title}</h3>
                <p className="mt-2.5 max-w-sm text-[15px] leading-relaxed text-muted-foreground">{s.text}</p>
              </FadeUp>
            </li>
          ))}
        </ol>
        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-border pt-8">
          <ArrowLink to="/leig-rom" data-testid="home-steps-booking-link">Start ein førespurnad</ArrowLink>
          <ArrowLink to="/kalender" data-testid="home-steps-calendar-link">Sjå kalenderen</ArrowLink>
          <ArrowLink to="/arranger" data-testid="home-steps-arranger-link">Arrangere for publikum?</ArrowLink>
        </div>
      </Section>

      {/* 05 ABOUT --------------------------------------------------- */}
      <Section id="om-huset" num="05" eyebrow="Om huset">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          <FadeUp className="lg:col-span-5">
            <Photo src={aboutImg} alt="Halsnøy Kulturhus i Sæbøvik" width={1200} sizes="(min-width: 1440px) 560px, (min-width: 1024px) 40vw, 100vw" className="img-hover aspect-[3/2] lg:aspect-[5/6]" imgClassName="[object-position:38%_50%]" testId="home-about-photo">
              <PhotoNumberTag src={aboutImg} interactive className="absolute right-2.5 top-2.5" testId="home-about-number" />
            </Photo>
          </FadeUp>
          <div className="flex flex-col justify-center lg:col-span-7">
            <div>
              <h2 className="t-h2 text-balance">{om?.title || "Eigd av folket. Drive på dugnad."}</h2>
              <p className="t-lead mt-6 max-w-xl text-muted-foreground text-pretty" data-testid="home-about-text">{paragraphs(om?.body)[0]}</p>
              {paragraphs(om?.body)[1] && <p className="mt-4 max-w-xl text-[16px] leading-relaxed text-muted-foreground text-pretty">{firstSentences(paragraphs(om?.body)[1], 2)}</p>}
              <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                <ArrowLink to="/om-oss" data-testid="home-about-link">Om huset og styret</ArrowLink>
                <ArrowLink to="/velforeininga" data-testid="home-volunteer-link">Slik kan du bidra</ArrowLink>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* 06 AREA ---------------------------------------------------- */}
      <Section id="omradet" num="06" eyebrow="Området" title={<>Midt i Sæbøvik. Rett ved <em className="t-swash">sjøen</em>.</>} subtitle="Kulturhuset ligg midt i Sæbøvik, med båthamn, gjestebrygge, bubilplass, butikk og flotte turområde like utanfor døra." action={<ArrowLink to="/omradet" data-testid="home-area-link">Utforsk området</ArrowLink>} className="pt-4 sm:pt-6 lg:pt-8">
        <FadeUp>
          <Photo src={panorama} alt="Sæbøvik og kulturhuset sett frå lufta" width={1600} sizes={SIZES.full} className="img-hover aspect-[3/2] sm:aspect-[21/9]" testId="home-panorama">
            <PhotoNumberTag src={panorama} interactive className="absolute right-2.5 top-2.5" testId="home-panorama-number" />
            <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/60 to-transparent px-5 pb-4 pt-14 text-paper sm:px-7 sm:pb-6">
              <span className="t-mono text-paper/75">Sæbøvik frå lufta</span>
              <span className="mt-1 block max-w-2xl font-display text-[clamp(18px,1.6vw,26px)] font-semibold tracking-[-0.02em] text-balance">Gjestebrygge, bubilplass og turløyper – alt i nærleiken</span>
            </figcaption>
          </Photo>
        </FadeUp>
        <ul className="mt-3 grid gap-x-3 gap-y-8 sm:grid-cols-3" data-testid="home-area-links">
          {areaLinks.map(({ key, to, alt }, i) => {
            const c = content?.[key];
            const img = c?.images?.[0];
            return (
              <li key={key}>
                <FadeUp delay={i * 0.06}>
                  <Link to={to} className="group block" data-testid={`home-area-${key}`}>
                    {img && <Photo src={img} alt={alt} width={800} sizes={SIZES.third} className="img-hover aspect-[4/3]" />}
                    <div className="mt-4 flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h3 className="t-h3 u-hover">{c?.title}</h3>
                        <p className="mt-1.5 text-[15px] leading-snug text-muted-foreground line-clamp-2">{paragraphs(c?.body)[0]}</p>
                      </div>
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary transition-colors group-hover:bg-foreground group-hover:text-background"><ArrowUpRight className="h-4 w-4" /></span>
                    </div>
                  </Link>
                </FadeUp>
              </li>
            );
          })}
        </ul>
      </Section>

      {/* 07 NEWS – only when the board has published something ------ */}
      {(posts?.items?.length || 0) > 0 && (
        <Section id="nytt" num="07" eyebrow="Nytt frå huset" title={<>Siste <em className="t-swash">nytt</em> frå huset</>} subtitle="Små og store saker frå scena, kafeen og dugnadsgjengen." action={<ArrowLink to="/nytt" data-testid="home-news-link">Alle saker</ArrowLink>} className="pt-4 sm:pt-6 lg:pt-8">
          <div className="grid gap-x-8 gap-y-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="home-news">
            {posts.items.slice(0, 3).map((p, i) => <FadeUp key={p.id} delay={i * 0.06}><PostCard post={p} testId={`home-post-${p.slug}`} /></FadeUp>)}
          </div>
        </Section>
      )}
    </div>
  );
}
